/**
 * STAGING-LAUNCH-READINESS-002-R2-R9 — single canonical customer order action registry.
 * CUSTOMER-COMMS-140-R1 — FOP/COD hierarchy + role tokens for unpaid detail.
 *
 * Unpaid NovaPay (active attempt):
 * continue → change → check → abandon → manager → cancel (bottom)
 *
 * Unpaid bank transfer (order_detail):
 * send_receipt → change → manager → to_catalog → cancel
 * (requisites already expanded — no view_payment_details)
 * Unpaid COD (order_detail):
 * view_delivery → change → manager → to_catalog → cancel
 */
export type CustomerOrderActionId =
  | "continue_payment"
  | "send_receipt"
  | "view_payment_details"
  | "view_delivery"
  | "check_payment_status"
  | "change_payment_method"
  | "abandon_payment"
  | "cancel_order"
  | "contact_manager"
  | "request_return"
  | "request_cancellation"
  | "return_to_order"
  | "to_catalog";

export type CustomerOrderActionRole =
  | "primary"
  | "accent"
  | "secondary"
  | "tertiary"
  | "danger";

export type CustomerOrderRouteContext =
  | "order_detail"
  | "success_unpaid"
  | "payment_return_bridge"
  | "other";

export type CustomerOrderActionsInput = {
  orderStatus: string | null | undefined;
  orderTopStatus?: string | null | undefined;
  paymentStatus: string | null | undefined;
  paymentMethod: string | null | undefined;
  paymentIntentStatus?: string | null | undefined;
  fulfillmentStatus?: string | null | undefined;
  routeContext: CustomerOrderRouteContext;
  /** Backend allowed_methods length — do not infer from frontend flags. */
  allowedMethodsCount?: number;
  /** True when packing/shipment/TTN already started — cancels order cancel. */
  packingOrShipmentStarted?: boolean;
  globalManagerCtaPresent?: boolean;
  /** RC7 — hide continue/retry that would create a new NovaPay session. */
  novapayCheckoutDegraded?: boolean;
  /** RC7 — provider still processing (prefer status-check over continue). */
  providerProcessing?: boolean;
};

export type CustomerOrderActionsResult = {
  actions: CustomerOrderActionId[];
  ownsManagerCta: boolean;
  showReturnToOrder: boolean;
};

const PAID = new Set([
  "paid",
  "payment_confirmed",
  "payment_confirmed_manual",
  "payment_confirmed_bank_iban",
  "paid_bank_iban",
  "payment_captured",
  "confirmed",
  "settled",
  "authorized_hold",
]);
const CANCELLED = new Set(["canceled", "cancelled"]);
const FULFILLMENT_BLOCKED = new Set([
  "packing",
  "ready_to_ship",
  "shipped",
  "completed",
  "delivered",
  "received",
  "collected",
]);
const RETURN_PROHIBITED = new Set([
  "completed",
  "delivered",
  "received",
  "collected",
  "canceled",
  "cancelled",
  "refunded",
  "returned",
]);
/** Recoverable unpaid statuses — include abandoned/expired NovaPay hang. */
const UNPAID_RECOVERABLE = new Set([
  "payment_pending",
  "pending",
  "payment_expired_novapay",
  "unpaid",
  "awaiting_payment",
  "",
]);

function norm(v: string | null | undefined): string {
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

export function isPaidPaymentStatus(
  paymentStatus: string | null | undefined,
  bankConfirmedAt?: string | null,
): boolean {
  if (String(bankConfirmedAt ?? "").trim()) return true;
  const s = norm(paymentStatus);
  return PAID.has(s) || s.startsWith("paid_");
}

export function isCancelledOrderStatus(
  orderStatus: string | null | undefined,
  orderTopStatus?: string | null | undefined,
): boolean {
  return CANCELLED.has(norm(orderTopStatus)) || CANCELLED.has(norm(orderStatus));
}

export function isUnpaidRecoverableStatus(paymentStatus: string | null | undefined): boolean {
  if (isPaidPaymentStatus(paymentStatus)) return false;
  const s = norm(paymentStatus);
  if (s === "payment_in_review") return false;
  return UNPAID_RECOVERABLE.has(s) || s.length === 0 || !s.startsWith("paid");
}

export function canRequestReturn(input: {
  paymentStatus: string | null | undefined;
  orderStatus: string | null | undefined;
  orderTopStatus?: string | null | undefined;
  fulfillmentStatus?: string | null | undefined;
}): boolean {
  if (!isPaidPaymentStatus(input.paymentStatus)) return false;
  if (isCancelledOrderStatus(input.orderStatus, input.orderTopStatus)) return false;
  const top = norm(input.orderTopStatus ?? input.orderStatus);
  const fulfill = norm(input.fulfillmentStatus);
  if (RETURN_PROHIBITED.has(top) || RETURN_PROHIBITED.has(fulfill)) return false;
  return true;
}

function hasActiveNovapayAttempt(intent: string): boolean {
  if (!intent) return true; // unknown → allow abandon for unpaid NovaPay hang recovery
  return intent === "pending" || intent === "creating" || intent === "awaiting_payment" || intent === "link_ready";
}

/**
 * Derive visible actions exactly once.
 */
export function getCustomerOrderActions(input: CustomerOrderActionsInput): CustomerOrderActionsResult {
  const paid = isPaidPaymentStatus(input.paymentStatus);
  const cancelled = isCancelledOrderStatus(input.orderStatus, input.orderTopStatus);
  const method = norm(input.paymentMethod);
  const intent = norm(input.paymentIntentStatus);
  const payRecoverable =
    !paid &&
    !cancelled &&
    (isUnpaidRecoverableStatus(input.paymentStatus) ||
      intent === "pending" ||
      intent === "creating" ||
      intent === "awaiting_payment" ||
      intent === "abandoned" ||
      intent === "failed" ||
      intent === "expired");
  const isNovapay = method === "novapay_card" || method === "";
  const isFop = method === "bank_transfer_fop";
  const isCod = method === "nova_poshta_cod";
  const top = norm(input.orderTopStatus ?? input.orderStatus);
  const fulfillBlocked =
    input.packingOrShipmentStarted === true ||
    FULFILLMENT_BLOCKED.has(top) ||
    FULFILLMENT_BLOCKED.has(norm(input.fulfillmentStatus));
  const canChangeMethod = (input.allowedMethodsCount ?? 1) > 1;
  const canCancelOrder = !paid && !cancelled && !fulfillBlocked;
  const canRequestCancellation = paid && !cancelled && !fulfillBlocked;
  const onOrderDetail = input.routeContext === "order_detail";
  const onSuccessUnpaid = input.routeContext === "success_unpaid";
  const onBridge = input.routeContext === "payment_return_bridge";

  const ordered: CustomerOrderActionId[] = [];
  const add = (id: CustomerOrderActionId) => {
    if (!ordered.includes(id)) ordered.push(id);
  };

  // CUSTOMER-COMMS-140-R3 — FOP receipt CTA is first-class on success (owned by success stack).
  // Other methods keep empty registry; success screen owns the one-column layout.
  if (onSuccessUnpaid) {
    if (isFop && payRecoverable) {
      add("contact_manager");
      add("to_catalog");
      return { actions: ordered, ownsManagerCta: true, showReturnToOrder: false };
    }
    return { actions: [], ownsManagerCta: false, showReturnToOrder: false };
  }

  if (cancelled) {
    if (onOrderDetail) add("to_catalog");
    add("contact_manager");
    return { actions: ordered, ownsManagerCta: true, showReturnToOrder: false };
  }

  if (paid) {
    if (isFop) add("send_receipt");
    if (canRequestCancellation) add("request_cancellation");
    else if (canRequestReturn(input)) add("request_return");
    if (onOrderDetail) add("to_catalog");
    add("contact_manager");
    return { actions: ordered, ownsManagerCta: true, showReturnToOrder: false };
  }

  if (isNovapay && (payRecoverable || onOrderDetail)) {
    const degraded = input.novapayCheckoutDegraded === true || input.providerProcessing === true;
    if (!degraded)     add("continue_payment");
    if (canChangeMethod) add("change_payment_method");
    add("check_payment_status");
    if (hasActiveNovapayAttempt(intent) && !degraded) add("abandon_payment");
    add("contact_manager");
    if (onOrderDetail) add("to_catalog");
  } else if (isFop && (payRecoverable || onOrderDetail)) {
    add("send_receipt");
    if (payRecoverable && canChangeMethod) add("change_payment_method");
    add("contact_manager");
    if (onOrderDetail) add("to_catalog");
  } else if (isCod && (payRecoverable || onOrderDetail)) {
    if (onOrderDetail) add("view_delivery");
    if (payRecoverable && canChangeMethod) add("change_payment_method");
    add("contact_manager");
    if (onOrderDetail) add("to_catalog");
  } else if (payRecoverable || onOrderDetail) {
    if (payRecoverable && canChangeMethod) add("change_payment_method");
    add("contact_manager");
    if (onOrderDetail) add("to_catalog");
  } else {
    add("contact_manager");
  }

  if (canCancelOrder) add("cancel_order");

  if (onBridge) add("return_to_order");

  const actions = ordered.filter((a) => !(a === "return_to_order" && onOrderDetail));

  return {
    actions,
    ownsManagerCta: actions.includes("contact_manager"),
    showReturnToOrder: onBridge && actions.includes("return_to_order"),
  };
}

export function actionRole(id: CustomerOrderActionId): CustomerOrderActionRole {
  switch (id) {
    case "continue_payment":
    case "send_receipt":
    case "view_delivery":
    case "return_to_order":
      return "primary";
    case "view_payment_details":
      return "accent";
    case "check_payment_status":
    case "change_payment_method":
    case "abandon_payment":
    case "contact_manager":
    case "request_return":
    case "request_cancellation":
      return "secondary";
    case "to_catalog":
      return "tertiary";
    case "cancel_order":
      return "danger";
    default:
      return "secondary";
  }
}

export function actionLabelUa(id: CustomerOrderActionId): string {
  switch (id) {
    case "continue_payment":
      return "Продовжити оплату";
    case "send_receipt":
      return "📎 Прикріпити квитанцію";
    case "view_payment_details":
      return "Переглянути реквізити";
    case "view_delivery":
      return "Переглянути доставку";
    case "check_payment_status":
      return "Перевірити статус оплати";
    case "change_payment_method":
      return "Змінити спосіб оплати";
    case "abandon_payment":
      return "Відмінити оплату";
    case "cancel_order":
      return "Скасувати замовлення";
    case "contact_manager":
      return "Зв’язатися з менеджером";
    case "request_return":
      return "Запросити повернення";
    case "request_cancellation":
      return "Запросити скасування";
    case "return_to_order":
      return "Повернутися до замовлення";
    case "to_catalog":
      return "До каталогу";
    default:
      return id;
  }
}

export function actionLabelEn(id: CustomerOrderActionId): string {
  switch (id) {
    case "continue_payment":
      return "Continue payment";
    case "send_receipt":
      return "📎 Attach receipt";
    case "view_payment_details":
      return "View payment details";
    case "view_delivery":
      return "View delivery";
    case "check_payment_status":
      return "Check payment status";
    case "change_payment_method":
      return "Change payment method";
    case "abandon_payment":
      return "Cancel payment attempt";
    case "cancel_order":
      return "Cancel order";
    case "contact_manager":
      return "Contact manager";
    case "request_return":
      return "Request a return";
    case "request_cancellation":
      return "Request cancellation";
    case "return_to_order":
      return "Return to order";
    case "to_catalog":
      return "To catalog";
    default:
      return id;
  }
}

export function assertUniqueActions(actions: CustomerOrderActionId[]): boolean {
  return new Set(actions).size === actions.length;
}

/** Guard helper — unpaid NovaPay order_detail sequence (active attempt). */
export function unpaidNovapayActionSequence(): CustomerOrderActionId[] {
  return [
    "continue_payment",
    "change_payment_method",
    "check_payment_status",
    "abandon_payment",
    "contact_manager",
    "to_catalog",
    "cancel_order",
  ];
}

export function unpaidFopActionSequence(): CustomerOrderActionId[] {
  return ["contact_manager", "to_catalog", "cancel_order"];
}

export function unpaidCodActionSequence(): CustomerOrderActionId[] {
  return ["view_delivery", "change_payment_method", "contact_manager", "to_catalog", "cancel_order"];
}
